/**
 * 跨瀏覽器語言偵測工具
 * 支援：Safari, Chrome, Firefox, Edge, Line Browser, WeChat Browser, Facebook Browser 等
 */

// 支援的語言列表
export const supportedLngs = ['zh-TW', 'zh', 'en', 'ja', 'vi'];

/**
 * 從各種來源獲取瀏覽器/系統語言
 * 支援各種瀏覽器和 WebView（Line, WeChat, Facebook 等）
 */
export const getBrowserLanguages = (): string[] => {
	const languages: string[] = [];

	try {
		// 1. navigator.languages - 最完整的語言列表（大多數現代瀏覽器支援）
		if (navigator.languages && navigator.languages.length > 0) {
			languages.push(...navigator.languages);
		}

		// 2. navigator.language - 主要語言（所有瀏覽器都支援）
		if (navigator.language) {
			languages.push(navigator.language);
		}

		// 3. navigator.userLanguage - IE/舊版 Edge 支援
		if ((navigator as any).userLanguage) {
			languages.push((navigator as any).userLanguage);
		}

		// 4. navigator.browserLanguage - IE 支援
		if ((navigator as any).browserLanguage) {
			languages.push((navigator as any).browserLanguage);
		}

		// 5. navigator.systemLanguage - IE 支援
		if ((navigator as any).systemLanguage) {
			languages.push((navigator as any).systemLanguage);
		}

		// 6. 從 HTML lang 屬性獲取
		const htmlLang = document.documentElement.lang;
		if (htmlLang) {
			languages.push(htmlLang);
		}

		// 7. 從 URL 參數獲取（某些應用可能通過 URL 傳遞語言）
		const urlParams = new URLSearchParams(window.location.search);
		const urlLang = urlParams.get('lang') || urlParams.get('language') || urlParams.get('locale');
		if (urlLang) {
			languages.unshift(urlLang); // 優先使用 URL 參數
		}
	} catch (e) {
		console.warn('[detectBrowserLanguage] Error getting browser languages:', e);
	}

	// 去重並過濾空值
	return Array.from(new Set(languages.filter((lang) => lang && lang.trim())));
};

/**
 * 將瀏覽器語言代碼映射到支援的語言
 * @param lng 瀏覽器語言代碼（例如：zh-CN, zh-TW, ja-JP, vi-VN）
 * @returns 支援的語言代碼，如果不在列表中則返回 null
 */
export const mapToSupportedLanguage = (lng: string | undefined | null): string | null => {
	if (!lng) return null;

	// 標準化語言代碼
	const normalizedLng = lng.trim().toLowerCase();

	// 繁體中文相關（台灣、香港、澳門、繁體標記）
	if (
		normalizedLng === 'zh-tw' ||
		normalizedLng === 'zh_tw' ||
		normalizedLng.startsWith('zh-tw') ||
		normalizedLng.startsWith('zh_tw') ||
		normalizedLng === 'zh-hant' ||
		normalizedLng.startsWith('zh-hant') ||
		normalizedLng === 'zh-hk' ||
		normalizedLng.startsWith('zh-hk') ||
		normalizedLng === 'zh-mo' ||
		normalizedLng.startsWith('zh-mo') ||
		normalizedLng === 'zh-cht' || // 某些系統使用 CHT
		normalizedLng === 'zh-traditional'
	) {
		return 'zh-TW';
	}

	// 簡體中文相關（中國大陸、新加坡、簡體標記）
	if (
		normalizedLng === 'zh-cn' ||
		normalizedLng === 'zh_cn' ||
		normalizedLng.startsWith('zh-cn') ||
		normalizedLng.startsWith('zh_cn') ||
		normalizedLng === 'zh-hans' ||
		normalizedLng.startsWith('zh-hans') ||
		normalizedLng === 'zh-sg' ||
		normalizedLng.startsWith('zh-sg') ||
		normalizedLng === 'zh-chs' || // 某些系統使用 CHS
		normalizedLng === 'zh-simplified' ||
		normalizedLng === 'zh' // 純 zh 預設為簡體
	) {
		return 'zh';
	}

	// 日文相關
	if (normalizedLng === 'ja' || normalizedLng.startsWith('ja-') || normalizedLng.startsWith('ja_')) {
		return 'ja';
	}

	// 越南語相關
	if (normalizedLng === 'vi' || normalizedLng.startsWith('vi-') || normalizedLng.startsWith('vi_')) {
		return 'vi';
	}

	// 英文相關
	if (normalizedLng === 'en' || normalizedLng.startsWith('en-') || normalizedLng.startsWith('en_')) {
		return 'en';
	}

	// 不在支援列表中
	return null;
};

/**
 * 偵測瀏覽器/系統語言並映射到支援的語言
 * 不使用 localStorage，每次都從瀏覽器/系統偵測
 * @returns 支援的語言代碼，預設為 'en'
 */
export const detectBrowserLanguage = (): string => {
	// 獲取所有可能的瀏覽器語言
	const browserLanguages = getBrowserLanguages();

	// 嘗試找到第一個支援的語言
	for (const lang of browserLanguages) {
		const mappedLang = mapToSupportedLanguage(lang);
		if (mappedLang) {
			return mappedLang;
		}
	}

	// 如果沒有找到支援的語言，返回英文
	return 'en';
};

/**
 * 獲取瀏覽器類型（用於調試）
 */
export const getBrowserInfo = (): { name: string; isWebView: boolean; languages: string[] } => {
	const ua = navigator.userAgent.toLowerCase();
	const languages = getBrowserLanguages();

	let name = 'Unknown';
	let isWebView = false;

	if (ua.includes('line')) {
		name = 'Line Browser';
		isWebView = true;
	} else if (ua.includes('micromessenger') || ua.includes('wechat')) {
		name = 'WeChat Browser';
		isWebView = true;
	} else if (ua.includes('fban') || ua.includes('fbav') || ua.includes('fb_iab')) {
		name = 'Facebook Browser';
		isWebView = true;
	} else if (ua.includes('instagram')) {
		name = 'Instagram Browser';
		isWebView = true;
	} else if (ua.includes('twitter')) {
		name = 'Twitter Browser';
		isWebView = true;
	} else if (ua.includes('edg/')) {
		name = 'Edge';
	} else if (ua.includes('chrome') && !ua.includes('edg')) {
		name = 'Chrome';
	} else if (ua.includes('safari') && !ua.includes('chrome')) {
		name = 'Safari';
	} else if (ua.includes('firefox')) {
		name = 'Firefox';
	} else if (ua.includes('msie') || ua.includes('trident/')) {
		name = 'Internet Explorer';
	}

	return { name, isWebView, languages };
};

export default detectBrowserLanguage;
